import Vue from 'vue'
import store from '~/store'
import Meta from 'vue-meta'
import routes from './routes'
import Router from 'vue-router'
import axios from 'axios'
import { sync } from 'vuex-router-sync'
import { ADMIN_ROUTES, LAST_NON_ADMIN_ROUTE } from '~/constants'

Vue.use(Meta)
Vue.use(Router)

// The middleware for every page of the application.
const globalMiddleware = ['locale', 'check-auth', 'password-reset']

// Load middleware modules dynamically.
const routeMiddleware = resolveMiddleware(
  require.context('~/middleware', false, /.*\.js$/)
)

const router = createRouter()

sync(store, router)

export default router

/**
 * Create a new router instance.
 *
 * @return {Router}
 */
function createRouter () {
  const router = new Router({
    scrollBehavior,
    mode: 'history',
    routes
  })

  router.beforeEach(beforeEach)
  router.afterEach(afterEach)

  router.onError((error) => {
    if (isChunkLoadError(error)) {
      reloadIfNotRecentlyReloaded()
    }
  })

  return router
}

const CHUNK_RELOAD_KEY = 'chunkReloadAt'
const CHUNK_RELOAD_COOLDOWN_MS = 30000

function isChunkLoadError (error) {
  return error?.name === 'ChunkLoadError' ||
    /Loading( CSS)? chunk \S+ failed/.test(error?.message || '')
}

function reloadIfNotRecentlyReloaded () {
  const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0
  if (Date.now() - last < CHUNK_RELOAD_COOLDOWN_MS) return
  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))
  window.location.reload(true)
}

/**
 * Global router guard.
 *
 * @param {Route} to
 * @param {Route} from
 * @param {Function} next
 */
async function beforeEach (to, from, next) {
  const isAdminRoute = ADMIN_ROUTES?.includes(to.name)
  if (!isAdminRoute) {
    localStorage.setItem('isAdmin', false)
    localStorage.setItem('active_admin_view', false)
    localStorage.setItem('default_admin_view', false)
    const event = new Event('active_admin_view')
    document.dispatchEvent(event)
  }

  if (window.cancelSource) {
    window.cancelSource.cancel('Route change: API call canceled.')
  }
  window.cancelSource = axios.CancelToken.source()

  let components = []

  try {
    // Get the matched components and resolve them.
    components = await resolveComponents(
      router.getMatchedComponents({ ...to })
    )
  } catch (error) {
    if (isChunkLoadError(error)) {
      reloadIfNotRecentlyReloaded()
      return
    }
  }

  if (components.length === 0) {
    return next()
  }

  // Start the loading bar.
  if (components[components.length - 1].loading !== false) {
    router.app.$nextTick(() => router.app.$loading.start())
  }

  // Get the middleware for all the matched components.
  const middleware = getMiddleware(components)

  // Load async data for all the matched components.
  await asyncData(components)

  // Call each middleware.
  callMiddleware(middleware, to, from, (...args) => {
    // Set the application layout only if "next()" was called with no args.
    if (args.length === 0) {
      router.app.setLayout(components[0].layout || '')
    }

    next(...args)
  })
}

/**
 * @param  {Array} components
 * @return {Promise<void>
 */
async function asyncData (components) {
  for (let i = 0; i < components.length; i++) {
    const component = components[i]

    if (!component.asyncData) {
      continue
    }

    const dataFn = component.data

    try {
      const asyncData = await component.asyncData()

      component.data = function () {
        return {
          ...(dataFn ? dataFn.apply(this) : {}),
          ...asyncData
        }
      }
    } catch (e) {
      component.layout = 'error'

      console.error('Failed to load asyncData', e)
    }
  }
}

/**
 * Global after hook.
 *
 * @param {Route} to
 * @param {Route} from
 * @param {Function} next
 */
async function afterEach (to, from, next) {
  await router.app.$nextTick()

  router.app.$loading.finish()
  if (!ADMIN_ROUTES.includes(to.name)) {
    localStorage.setItem(LAST_NON_ADMIN_ROUTE, to.name)
  }
}

/**
 * Call each middleware.
 *
 * @param {Array} middleware
 * @param {Route} to
 * @param {Route} from
 * @param {Function} next
 */
function callMiddleware (middleware, to, from, next) {
  const stack = middleware.reverse()

  const _next = (...args) => {
    // Stop if "_next" was called with an argument or the stack is empty.
    if (args.length > 0 || stack.length === 0) {
      if (args.length > 0) {
        router.app.$loading.finish()
      }

      return next(...args)
    }

    const { middleware, params } = parseMiddleware(stack.pop())

    if (typeof middleware === 'function') {
      middleware(to, from, _next, params)
    } else if (routeMiddleware[middleware]) {
      routeMiddleware[middleware](to, from, _next, params)
    } else {
      throw Error(`Undefined middleware [${middleware}]`)
    }
  }

  _next()
}

/**
 * @param  {String|Function} middleware
 * @return {Object}
 */
function parseMiddleware (middleware) {
  if (typeof middleware === 'function') {
    return { middleware }
  }

  const [name, params] = middleware.split(':')

  return { middleware: name, params }
}

/**
 * Resolve async components.
 *
 * @param  {Array} components
 * @return {Array}
 */
function resolveComponents (components) {
  return Promise.all(components.map(component => {
    return typeof component === 'function' ? component() : component
  }))
}

/**
 * Merge the the global middleware with the components middleware.
 *
 * @param  {Array} components
 * @return {Array}
 */
function getMiddleware (components) {
  const middleware = [...globalMiddleware]

  components.filter(c => c.middleware).forEach(component => {
    if (Array.isArray(component.middleware)) {
      middleware.push(...component.middleware)
    } else {
      middleware.push(component.middleware)
    }
  })

  return middleware
}

/**
 * Scroll Behavior
 *
 * @link https://router.vuejs.org/en/advanced/scroll-behavior.html
 *
 * @param  {Route} to
 * @param  {Route} from
 * @param  {Object|undefined} savedPosition
 * @return {Object|Promise<void>}
 */
function scrollBehavior (to, from, savedPosition) {
  if (savedPosition) {
    return savedPosition
  }

  if (to.hash) {
    return { selector: to.hash }
  }

  const [component] = router.getMatchedComponents({ ...to }).slice(-1)

  if (component && component.scrollToTop === false) {
    return {}
  }

  if (avoidScrollToTop(to, from, savedPosition)) {
    return Promise.resolve()
  }

  return new Promise((resolve, reject) => {
    setTimeout(() => {
      resolve({ x: 0, y: 0 })
    }, 190)
  })
}

/**
 * @param  {Route} to
 * @param  {Route} from
 * @param  {Object|undefined} savedPosition
 * @return {Boolean}
 */
function avoidScrollToTop (to, from, savedPosition) {
  const excludedRouteNames = [
    'products',
    'collections',
    'tag-module'
  ]

  const excludedRouteParams = [
    'availability',
    'brand',
    'sub_brand',
    'category',
    'department',
    'season',
    'color',
    'pattern',
    'size'
  ]

  const queries = Object.keys(to.query)

  // Keep the scroll position for any filter-driven navigation (the filter drawer
  // refreshes results in the background), regardless of query-key ordering.
  return excludedRouteNames.includes(to.name) && queries.some(q => excludedRouteParams.includes(q))
}

/**
 * @param  {Object} requireContext
 * @return {Object}
 */
function resolveMiddleware (requireContext) {
  return requireContext.keys()
    .map(file =>
      [file.replace(/(^.\/)|(\.js$)/g, ''), requireContext(file)]
    )
    .reduce((guards, [name, guard]) => (
      { ...guards, [name]: guard.default }
    ), {})
}

const originalPush = Router.prototype.push
Router.prototype.push = function push (location) {
  return originalPush.call(this, location).catch(err => {
    if (Router.isNavigationFailure && Router.isNavigationFailure(err)) return err
    if (err && err.name === 'NavigationDuplicated') return err
    throw err
  })
}
